import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {User, UserId} from '@app/layout/admin/services/api/user-interfaces';
import {TicketDataCaches} from '@app/shared/data-stores/ticket-data-caches';
import {SiteInfo} from '@app/shared/services/api/access-sites.interface';
import {AlertTicket, EscalatedTicket, EscalatedTicketCreateBody, TicketStatusId, TicketType, resolvedActionStatuses, TicketPriorityId} from '@app/shared/services/api/tickets/tickets.interface';
import {Subject, combineLatest, distinctUntilChanged, finalize, map, shareReplay, startWith, takeUntil} from 'rxjs';
import {TicketUpsertData, TicketUpsertForm} from '../tickets.interface';
import {UserProfileService} from '@app/shared/services/api/user-profile.service';
import {TicketUtils} from '../ticket-utils';
import {I18nMessage} from '@app/shared/api/model/i18n';
import {CommentMentionItem} from '../comment-mention/comment-mention.directive';
import {CollectionUtils} from '@app/shared/util/collections/collection-utils';
import {BrowserUtils} from '@app/shared/util/general/browser-utils';


enum SimpleEscalatedUpsertTab {
  ASSIGN = 'Assign',
  RESOLVE = 'Resolve'
}


interface SimpleUpsertFormGroupValue {
  selectedTabControl: SimpleEscalatedUpsertTab;
  assigneeId: UserId;
  statusId: TicketStatusId;
  comment: string;
}


type SimpleUpsertFormGroup = {[key in keyof SimpleUpsertFormGroupValue]: FormControl<SimpleUpsertFormGroupValue[key]>};


@Component({
  selector: 'app-escalated-ticket-simple-upsert',
  templateUrl: './escalated-ticket-simple-upsert.component.html',
  styleUrls: ['./escalated-ticket-simple-upsert.component.scss'],
  standalone: false
})
export class EscalatedTicketSimpleUpsertComponent implements OnInit, OnChanges, OnDestroy, TicketUpsertForm {
  @Input() site: SiteInfo | null;
  @Input() ticket: EscalatedTicket | null;
  @Output() ticketUpsertData = new EventEmitter<TicketUpsertData>();
  @Output() loading = new EventEmitter<boolean>();
  @Output() pending = new EventEmitter<boolean>();
  @ViewChild('commentBox') commentBoxElementRef: ElementRef<HTMLTextAreaElement>;

  SimpleEscalatedUpsertTab = SimpleEscalatedUpsertTab;
  formGroup: FormGroup<SimpleUpsertFormGroup>;
  assigneesById: Map<UserId, User>;
  assigneeOptions: User[];
  assigneesByEntityName: Map<string, User[]>;
  assigneesLoading = false;
  commentBoxFocused = false;
  mentionLastFocused = false;
  hasCommentText = false;
  isMobile: boolean;
  mobileMentionControl: FormControl<User>;
  statusOptions = resolvedActionStatuses;
  readonly assigneeErrorMessages: Record<string, I18nMessage> = {required: {messageKey: 'Select an assignee, or assign to yourself'}};
  readonly MENTION_CHARACTER = TicketUtils.MENTION_CHARACTER;
  private commentBoxFocusedDelayHandle: ReturnType<typeof setTimeout> | null = null;
  private readonly ngUnsubscribe = new Subject<void>();
  private readonly loading$ = this.loading.asObservable().pipe(shareReplay({refCount: true, bufferSize: 1}));

  constructor(private ticketDataCaches: TicketDataCaches,
              private userProfileService: UserProfileService
  ) {
    // makes loading$ hot so it does not miss any emit.
    this.loading$.pipe(takeUntil(this.ngUnsubscribe)).subscribe();
  }

  ngOnInit() {
    this.isMobile = BrowserUtils.isMobile();
    this.formGroup = new FormGroup<SimpleUpsertFormGroup>({
      selectedTabControl: new FormControl(SimpleEscalatedUpsertTab.ASSIGN),
      assigneeId: new FormControl(null, Validators.required),
      statusId: new FormControl(TicketStatusId.STATUS_RESOLVED, Validators.required),
      comment: new FormControl(null)
    });
    if (this.isMobile) {
      this.mobileMentionControl = new FormControl<User>(null);
      this.mobileMentionControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(assignee => {
        this.addMention(assignee);
        this.mobileMentionControl.reset(null, {emitEvent: false});
      });
    }

    this.formGroup.statusChanges.pipe(
      map(() => this.formGroup.pending),
      distinctUntilChanged(),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(pending => this.pending.emit(pending));

    combineLatest([
      this.loading$,
      this.formGroup.valueChanges.pipe(startWith(this.formGroup.value))
    ]).pipe(takeUntil(this.ngUnsubscribe)).subscribe(([loading]) => {
      this.enableDisableControls();
      const commentValue = this.formGroup.controls.comment.value?.trim();
      this.hasCommentText = !!commentValue;
      if (!loading) {
        let emitValue: TicketUpsertData = null;
        if (this.formGroup.valid) {
          const ticketBody = this.buildTicketCreateBody(this.ticket, <SimpleUpsertFormGroupValue>this.formGroup.value);
          emitValue = {
            site: this.site,
            ticketBody,
            assigneesById: this.assigneesById,
            linkedAlerts: this.ticket.ticket_links.map(ticketLink => <AlertTicket>ticketLink.ticket)
          };
          if (commentValue) {
            emitValue.comment = commentValue;
          }
        }
        this.ticketUpsertData.emit(emitValue);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.site) {
      this.loadAssignees(this.site?.site_id);
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  groupByFn(assignee: User): string | null {
    return assignee?.entity?.name ?? null;
  }

  showErrors() {
    Object.values(this.formGroup.controls).forEach((control) => {
      if (control.errors && !control.touched) {
        control.markAsTouched();
      }
    });
  }

  /** cancels click event in case user clicks on group item */
  mentionItemClickHandler(item: CommentMentionItem, event: MouseEvent): void | boolean {
    if (item && (item.isGroup || item.isSpinner)) {
      event.stopPropagation();
      return false;
    }
  }

  onCommentBoxFocus(focused: boolean): void {
    if (this.commentBoxFocusedDelayHandle != null) {
      clearTimeout(this.commentBoxFocusedDelayHandle);
    }
    // 200ms defer keeps the @Mention button alive between mousedown and click. Without it, clicking the button blurs the textarea
    // this handler hides the button before mouseup fires, and the click never opens the dropdown. Verified locally.
    this.commentBoxFocusedDelayHandle = setTimeout(() => {
      this.commentBoxFocused = focused;
      this.commentBoxFocusedDelayHandle = null;
    }, 200);
  }

  addMention(assignee: User): void {
    if (!assignee?.email) {
      return;
    }
    const mention = this.MENTION_CHARACTER + assignee.email;
    const commentControl = this.formGroup.controls.comment;
    const current = commentControl.value ?? '';
    const textarea = this.commentBoxElementRef.nativeElement;
    const cursorIndex = textarea.selectionStart;
    const before = current.slice(0, cursorIndex);
    const needsLeadingSpace = before.length > 0 && !!before.charAt(before.length - 1).trim();
    commentControl.setValue(before + (needsLeadingSpace ? ' ' : '') + mention + ' ' + current.slice(cursorIndex));
    textarea.focus();
  }

  updateMentionLastFocus(lastFocused: boolean): void {
    this.mentionLastFocused = lastFocused;
  }

  private loadAssignees(siteId: number): void {
    if (siteId != null) {
      this.updateAssigneeLoading(true);
      this.ticketDataCaches.getAssigneesBySiteId(siteId).pipe(
        finalize(() => this.updateAssigneeLoading(false)),
        takeUntil(this.ngUnsubscribe)
      ).subscribe(assigneesByIdResponse => {
        this.assigneesById = new Map(assigneesByIdResponse);
        this.assigneeOptions = Array.from(this.assigneesById.values());
        this.assigneesByEntityName = CollectionUtils.groupByFn(this.assigneesById.values(), a => a.entity?.name ?? null);
      });
    } else {
      this.assigneesById = null;
      this.assigneeOptions = null;
      this.assigneesByEntityName = null;
    }
  }

  private updateAssigneeLoading(loading: boolean) {
    this.assigneesLoading = loading;
    this.loading.emit(this.assigneesLoading);
  }

  private enableDisableControls() {
    if (this.formGroup.value.selectedTabControl === SimpleEscalatedUpsertTab.ASSIGN) {
      if (this.formGroup.controls.assigneeId.disabled) {
        this.formGroup.controls.assigneeId.enable({emitEvent: false});
      }
      if (this.formGroup.controls.statusId.enabled) {
        this.formGroup.controls.statusId.disable({emitEvent: false});
      }
    } else if (this.formGroup.value.selectedTabControl === SimpleEscalatedUpsertTab.RESOLVE) {
      if (this.formGroup.controls.assigneeId.enabled) {
        this.formGroup.controls.assigneeId.disable({emitEvent: false});
      }
      if (this.formGroup.controls.statusId.disabled) {
        this.formGroup.controls.statusId.enable({emitEvent: false});
      }
    }
  }

  private buildTicketCreateBody(ticket: EscalatedTicket, formValue: SimpleUpsertFormGroupValue): EscalatedTicketCreateBody | null {
    const {site_id, summary, description, equipment_ids, solution, priority_id, priority, benefit, benefit_id, savings} = ticket;
    const linkedAlertIds = TicketUtils.getActionLinkedAlerts(ticket).map(alert => alert.ticket_id);
    let assigneeIds: UserId[];
    let statusId: TicketStatusId = null;
    if (formValue.selectedTabControl === SimpleEscalatedUpsertTab.ASSIGN) {
      assigneeIds = [formValue.assigneeId];
      statusId = TicketStatusId.STATUS_IN_PROGRESS;
    } else if (formValue.selectedTabControl === SimpleEscalatedUpsertTab.RESOLVE) {
      const userId: UserId = this.userProfileService.getLoggedInUser()?.userId;
      assigneeIds = (userId && this.assigneesById.has(userId)) ? [userId] : null;
      statusId = formValue.statusId;
    } else {
      throw new Error('Invalid tab selected in EscalatedTicketSimpleUpsertComponent');
    }

    return {
      type: TicketType.ESCALATED,
      site_id,
      equipment_ids,
      summary,
      description,
      solution,
      savings,
      status_id: statusId,
      priority_id: priority_id ?? priority?.priority_id ?? TicketPriorityId.PRIORITY_P3_ROUTINE,
      ...(benefit_id != null || benefit?.length) && {benefit_id: benefit_id ?? benefit?.[0]?.benefit_id},
      ...linkedAlertIds?.length && {source_ticket_ids: linkedAlertIds},
      ...assigneeIds?.length && {assignee_ids: assigneeIds}
    };
  }
}
